// 🎙 회의 녹음 전역 호스트 (2026-10-26 형 1단계) — App 에 1회 마운트(통화 독과 같은 자리). 진입점(입력창 ＋·프로필·프로젝트·카드)은
//   이벤트만 보낸다: tpt:meetrec-start {rid?, roomName?, title?, pjt?} → 시작 창 / tpt:meetrec-open {id?} → 회의록 창.
//   방 카드 링크 /chat?meetrec=<id> 도 여기서 받아 회의록 창을 연다. 녹음 중엔 화면 위 녹음 바(어느 화면에서든).
//   기능 스위치 'meet-recorder' · iOS 앱은 숨김(백그라운드 녹음 네이티브 작업은 맥 몫 — 우편함).
import { lazy, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useFeatureFlag } from '../../api/featureFlags';
import { isIosApp } from '../../utils/platform';
import { tptAlert, tptConfirm } from '../../stores/modalStore';
import { getPrefLang } from '../../api/translate';
import {
	MEET_REC_FLAG, MEET_REC_START_EVENT, MEET_REC_OPEN_EVENT, MEET_REC_STOP_REQUEST, type MeetRecStartReq,
	useMeetRecState, recElapsedSec, pauseMeetRecording, resumeMeetRecording, stopMeetRecording, nativeMeetRecAvailable, restoreNativeMeetRec,
} from '../../api/meetRec';
import MeetRecStartModal from './MeetRecStartModal';
import { Icon } from '../icons';

const MeetRecListModal = lazy(() => import('./MeetRecListModal'));

/** 🎙 녹음 가능 — iOS 는 네이티브 녹음 브릿지가 있는 앱 빌드만(맥 M-066), 그 밖은 기능 스위치대로 */
export function meetRecAvailable(flag: boolean): boolean {
	return flag && (!isIosApp() || nativeMeetRecAvailable());
}
/** 📖 회의록 보기(목록·재생·파일로 회의록) — 녹음 못 하는 iOS 옛 빌드에서도 열린다 (2026-10-27 형 "회의록 메뉴는 aab/ios 에서도") */
export function meetRecViewAvailable(flag: boolean): boolean {
	return flag;
}
const fmt = (sec: number) => `${Math.floor(sec / 3600) ? `${Math.floor(sec / 3600)}:` : ''}${String(Math.floor(sec / 60) % 60).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

export default function MeetRecHost() {
	const { t } = useTranslation();
	const authToken = useAuthStore((s) => s.authToken);
	const flag = useFeatureFlag(MEET_REC_FLAG);
	const [startReq, setStartReq] = useState<MeetRecStartReq | null>(null);
	const [openId, setOpenId] = useState<string | null | undefined>(undefined);   // undefined = 닫힘, null = 목록
	const rec = useMeetRecState();
	const [, tick] = useState(0);
	const loc = useLocation();
	const nav = useNavigate();
	// 🍎 iOS 앱이 녹음 중인데 웹이 새로 떴으면(재로드) 녹음 바를 되살린다 — 앱 상태 동기 읽기(맥 M-066)
	useEffect(() => { restoreNativeMeetRec(); }, []);

	useEffect(() => {
		const onStart = (e: Event) => setStartReq(((e as CustomEvent<MeetRecStartReq>).detail) || {});
		const onOpen = (e: Event) => setOpenId(((e as CustomEvent<{ id?: string }>).detail)?.id || null);
		window.addEventListener(MEET_REC_START_EVENT, onStart);
		window.addEventListener(MEET_REC_OPEN_EVENT, onOpen);
		return () => { window.removeEventListener(MEET_REC_START_EVENT, onStart); window.removeEventListener(MEET_REC_OPEN_EVENT, onOpen); };
	}, []);
	// 방 카드 링크 /chat?meetrec=<id> — 열고 주소에서 뺀다(뒤로 가기에 남지 않게)
	useEffect(() => {
		const sp = new URLSearchParams(loc.search);
		const id = sp.get('meetrec');
		if (!id || !/^mr-[a-z0-9]{8,40}$/.test(id)) return;
		setOpenId(id);
		sp.delete('meetrec');
		nav({ pathname: loc.pathname, search: sp.toString() ? `?${sp}` : '' }, { replace: true });
	}, [loc.search, loc.pathname, nav]);
	// 녹음 바 경과 시간
	useEffect(() => {
		if (rec.phase === 'idle') return;
		const h = setInterval(() => tick((n) => n + 1), 1000);
		return () => clearInterval(h);
	}, [rec.phase]);

	const stop = async (ask: boolean) => {
		if (ask && !(await tptConfirm(t('meetRec.stopConfirm', { defaultValue: '녹음을 끝내고 회의록을 만들까요?' }), { okLabel: t('meetRec.stop', { defaultValue: '녹음 끝내기' }) }))) return;
		try {
			const id = await stopMeetRecording({ lang: getPrefLang(), announce: t('meetRec.announceEnd', { defaultValue: '녹음을 마칩니다.' }) });
			if (id) void tptAlert(t('meetRec.stoppedNotice', { defaultValue: '녹음을 마쳤습니다. AI 회의록을 만드는 데 몇 분 걸리며, 끝나면 대화방(또는 나와의 대화)에 올라옵니다. 프로필 메뉴 > 회의록에서도 볼 수 있습니다.' }));
		} catch (e) { void tptAlert(t('meetRec.stopFail', { defaultValue: '녹음 종료 중 오류: {{msg}}', msg: (e as Error).message })); }
	};
	// 안드 알림 [녹음 끝내기]
	useEffect(() => {
		const h = () => { void stop(false); };
		window.addEventListener(MEET_REC_STOP_REQUEST, h);
		return () => window.removeEventListener(MEET_REC_STOP_REQUEST, h);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!authToken) return null;
	const sec = recElapsedSec(rec);
	return (
		<>
			{rec.phase !== 'idle' && (
				// 🎙 2줄 고정 (2026-10-27 형 "1줄=녹음중·시간, 하단 일시정지/끝내기, 아주 깔끔하게") — 폭 고정이라 좁은 화면에서도 3줄로 안 접힌다
				<div className="fixed left-1/2 top-2 z-[2300] w-[min(300px,calc(100vw-16px))] -translate-x-1/2 rounded-2xl border border-red-200 bg-surface px-3 py-2 shadow-lg" data-testid="meetrec-bar">
					<div className="flex items-center gap-2 whitespace-nowrap text-ui-sm">
						<span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-red-600 ${rec.phase === 'recording' ? 'animate-pulse' : 'opacity-40'}`} />
						<span className="shrink-0 font-semibold text-red-700">{rec.phase === 'paused' ? t('meetRec.paused', { defaultValue: '일시정지' }) : rec.phase === 'stopping' ? t('meetRec.stopping', { defaultValue: '마무리 중…' }) : rec.phase === 'starting' ? t('meetRec.starting', { defaultValue: '준비 중…' }) : t('meetRec.recording', { defaultValue: '녹음 중' })}</span>
						<span className="min-w-0 flex-1 truncate text-ui-xs text-fg-subtle" title={rec.title}>{rec.title}</span>
						{rec.pending > 1 && <span className="inline-flex shrink-0 items-center gap-0.5 text-ui-xs text-amber-600" title={rec.uploadError}><Icon name="refresh" size={11} />{rec.pending}</span>}
						<span className="shrink-0 font-mono font-semibold tabular-nums text-fg">{fmt(sec)}</span>
					</div>
					{(rec.phase === 'recording' || rec.phase === 'paused') && (
						<div className={`mt-2 grid gap-2 ${rec.native ? 'grid-cols-1' : 'grid-cols-2'}`}>
							{!rec.native && <button type="button" onClick={() => (rec.phase === 'paused' ? resumeMeetRecording() : pauseMeetRecording())} data-testid="meetrec-pause"
								className="inline-flex items-center justify-center gap-1 rounded-lg bg-surface-muted py-1 text-ui-xs font-semibold text-fg hover:bg-hover-strong">
								<Icon name={rec.phase === 'paused' ? 'play' : 'pause'} size={12} />{rec.phase === 'paused' ? t('meetRec.resume', { defaultValue: '계속' }) : t('meetRec.pause', { defaultValue: '일시정지' })}
							</button>}
							<button type="button" onClick={() => void stop(true)} data-testid="meetrec-stop"
								className="inline-flex items-center justify-center gap-1 rounded-lg bg-red-600 py-1 text-ui-xs font-semibold text-white hover:bg-red-700">
								<Icon name="square" size={11} />{t('meetRec.stopShort', { defaultValue: '끝내기' })}
							</button>
						</div>
					)}
				</div>
			)}
			{startReq && (startReq.upload ? meetRecViewAvailable(flag) : meetRecAvailable(flag)) && <MeetRecStartModal req={startReq} onClose={() => setStartReq(null)} />}
			{openId !== undefined && (
				<Suspense fallback={null}>
					<MeetRecListModal initialId={openId} onClose={() => setOpenId(undefined)} />
				</Suspense>
			)}
		</>
	);
}
